import { queryOptions, keepPreviousData } from '@tanstack/react-query'
import { apiClient } from '../client'
import { unwrapDraft } from '../transforms'
import type { PaginatedResponse, BlogDraft, DraftStatus, ImageCandidatesResponse, PromptSuggestionsResponse } from '../types'

// API returns SEO fields as flat properties (seoTitle/seoDescription/seoKeywords);
// the client expects nested `seo: { title, description, keywords }`. Translate at
// the boundary so components and forms keep their existing nested shape.
type RawListResponse = PaginatedResponse<Parameters<typeof unwrapDraft>[0]>
type RawDetailResponse = Parameters<typeof unwrapDraft>[0]

export const draftQueries = {
  list: (agentId: string, params?: { status?: DraftStatus; page?: number }) =>
    queryOptions({
      queryKey: ['drafts', 'list', agentId, params?.status ?? 'all', params?.page ?? 1] as const,
      queryFn: async () => {
        const searchParams = new URLSearchParams()
        if (params?.status) searchParams.set('status', params.status)
        searchParams.set('page', String(params?.page ?? 1))
        searchParams.set('page_size', '20')
        const raw = await apiClient<RawListResponse>(
          `/api/v1/agents/${agentId}/drafts?${searchParams.toString()}`,
        )
        return { ...raw, data: raw.data.map(unwrapDraft) } as PaginatedResponse<BlogDraft>
      },
      placeholderData: keepPreviousData,
    }),

  detail: (agentId: string, draftId: string) =>
    queryOptions({
      queryKey: ['drafts', 'detail', agentId, draftId] as const,
      queryFn: async () => {
        const raw = await apiClient<RawDetailResponse>(
          `/api/v1/agents/${agentId}/drafts/${draftId}`,
        )
        return unwrapDraft(raw)
      },
      staleTime: 1000 * 30,
    }),

  candidates: (agentId: string, draftId: string) =>
    queryOptions({
      queryKey: ['drafts', 'candidates', agentId, draftId] as const,
      queryFn: () =>
        apiClient<ImageCandidatesResponse>(
          `/api/v1/agents/${agentId}/drafts/${draftId}/candidates`,
        ),
      staleTime: 1000 * 60 * 5,
    }),

  promptSuggestions: (agentId: string, draftId: string) =>
    queryOptions({
      queryKey: ['drafts', 'promptSuggestions', agentId, draftId] as const,
      queryFn: () =>
        apiClient<PromptSuggestionsResponse>(
          `/api/v1/agents/${agentId}/drafts/${draftId}/candidates/prompt-suggestions`,
        ),
      staleTime: 1000 * 60 * 10,
    }),
}
